/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
import { PolicyUtilService } from '@amfam/policy/e-signature/data-access';
import {
  PolicyAnalytics,
  RiskModel,
  RiskSelectionContext,
  VehicleType
} from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehiclePolicyActions from '../../../core/store/replace-vehicle-policy/replace-vehicle-policy.actions';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';

@Component({
  selector: 'ds-replace-vehicle-risk-selection',
  templateUrl: './replace-vehicle-risk-selection.component.html',
  styleUrls: ['./replace-vehicle-risk-selection.component.scss'],
  standalone: false
})
export class ReplaceVehicleRiskSelectionComponent implements OnInit, AfterViewInit {
  @Output() cancelEvent: EventEmitter<any> = new EventEmitter();

  riskSelectionForm: UntypedFormGroup;
  riskSelectionCtrl: AbstractControl;
  loading$: Observable<boolean>;
  vehicles: RiskModel[];
  displayPolicyGracePeriod = false;
  public addVehicleLabel = this.copyService.getCopy('policy.addVehicleLabel');
  public replaceVehicleLabel = this.copyService.getCopy('policy.replaceVehicleLabel');
  public authorized$ = this.roleService.isAuthorized('replacevehicle_selectaddvehicle');
  public isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  public hasAdvanceAutoPolicy$ = this.store.select(PolicySelectors.selectHasAdvanceAutoPolicy);
  hasMultipleAdvanceAutoPolicies$ = this.store.select(
    PolicySelectors.selectHasMultipleAdvanceAutoPolicies
  );
  hasClassicPolicies$ = this.store.select(PolicySelectors.selectHasClassicPolicies);
  hasAdvancePolicies$ = this.store.select(PolicySelectors.selectHasAdvancePolicies);
  hasOnlyPolicyComprehensiveCoverage$ = this.store.select(
    PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage
  );
  hasAnyPolicyLiabilityCoverage$ = this.store.select(fromRoot.hasAnyPolicyLiabilityCoverage);
  gracePeriodDays: string;
  gracePeriodContent: string;
  readonly advancedGraceDays = '14';
  readonly classicGraceDays = '30';
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();
  private currentVehicle: RiskModel;

  constructor(
    private store: Store,
    private formBuilder: UntypedFormBuilder,
    private analyticsFacade: AnalyticsFacade,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    public roleService: ImpersonateRolesService,
    private policyUtilService: PolicyUtilService,
    private featureFlagService: FeatureFlagService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Add/Replace Vehicle');

    // List the risks
    this.loading$ = this.store.select(PolicySelectors.selectPoliciesLoading);

    this.store
      .select(fromRoot.getReplaceVehicleRisks)
      .pipe(takeUntil(this.stop$))
      .subscribe(data => {
        this.vehicles = data?.filter(risk => risk.vehicleType === VehicleType.Auto);
      });

    // Initialize the form
    this.buildForm();

    // Dispatch an action to re-initialize the replaceVehicle state
    this.store.dispatch(new replaceVehicleActions.ReplaceVehicleInitAction());
    this.setUpGracePeriodContent();
  }

  setUpGracePeriodContent() {
    this.hasAdvanceAutoPolicy$
      .pipe(
        takeUntil(this.stop$),
        map(hasAdvanceAutoPolicy =>
          hasAdvanceAutoPolicy ? this.advancedGraceDays : this.classicGraceDays
        ),
        tap(days => {
          this.gracePeriodDays = days;
          this.gracePeriodContent = this.copyService.getCopy('policy.gracePeriodContent', {
            gracePeriodDays: days
          });
        })
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyAddVehicleNewOrReplaceRiskSelection);
  }

  buildForm() {
    this.riskSelectionForm = this.formBuilder.group({});
  }

  riskSelectionSubmit() {
    const context = this.currentVehicle
      ? RiskSelectionContext.REPLACE_VEHICLE
      : RiskSelectionContext.ADD_VEHICLE;
    this.store.dispatch(new replaceVehicleActions.SetVehicleRiskSelectionContext(context));
    this.store.dispatch(
      new replaceVehiclePolicyActions.ReplaceVehiclePolicySelectAction({
        vehicleId: _get(this.currentVehicle, 'id'),
        policyNumber: _get(this.currentVehicle, 'policy.policyNumber'),
        effectiveDate: _get(this.currentVehicle, 'policy.periodEndDate')
      })
    );

    if (this.currentVehicle) {
      this.analyticsFacade.trackEvent({
        event: 'replace_vehicle_start',
        form_name: 'Replace Vehicle Flow',
        vehicle_make: this.currentVehicle.make,
        vehicle_model: this.currentVehicle.model,
        vehicle_year: this.currentVehicle.year,
        product_line: 'auto'
      });
    }

    const hasNonComprehensiveNonLiabilityCoverage = _get(
      this.currentVehicle,
      'vehicleCoverages',
      []
    ).some(coverage =>
      this.policyUtilService.isNonComprehensiveNonLiabilityCoverageVehicle(coverage)
    );

    combineLatest([
      this.store.select(fromRoot.isValidVehicleToBeReplaced(this.currentVehicle?.id)),
      this.store.select(BrandSelectors.selectIsPartner),
      this.store.select(PolicySelectors.selectHasClassicPolicies),
      this.store.select(PolicySelectors.selectHasAdvancePolicies),
      this.store.select(PolicySelectors.selectHasOnlyPolicyComprehensiveCoverage)
    ])
      .pipe(
        map(
          ([
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage
          ]) => ({
            isValidVehicleToBeReplaced,
            isPartner,
            hasClassicPolicy,
            advancePolicy,
            hasOnlyPolicyComprehensiveCoverage
          })
        ),
        take(1)
      )
      .subscribe(
        ({
          isValidVehicleToBeReplaced,
          isPartner,
          hasClassicPolicy,
          advancePolicy,
          hasOnlyPolicyComprehensiveCoverage
        }) => {
          const replaceVehicleSkipHardstop = this.featureFlagService.isEnabled(
            'replace_vehicle_skip_hardstop'
          );
          const isShowhardstopRiskSelectionPage =
            this.featureFlagService.isEnabled('add_vehicle') &&
            replaceVehicleSkipHardstop &&
            ((hasClassicPolicy && !advancePolicy && !isPartner) ||
              (advancePolicy && !isPartner && hasOnlyPolicyComprehensiveCoverage));

          if (isShowhardstopRiskSelectionPage && this.currentVehicle?.id) {
            return this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  `/policies/risk-selection/replace-vehicle-quick-quote/${this.currentVehicle.id}/classic-year-vin`
                ]
              })
            );
          }

          if (!isValidVehicleToBeReplaced && this.currentVehicle?.id) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: ['/policies/risk-selection', 'grace-period']
              })
            );
          } else if (
            this.currentVehicle !== undefined &&
            this.currentVehicle.id &&
            hasNonComprehensiveNonLiabilityCoverage
          ) {
            this.store.dispatch(new replaceVehicleActions.SetVehicleRiskSelectionContext(null));
            const pathName = this.featureFlagService.isEnabled('replace_vehicle_quickquote')
              ? '/policies/risk-selection/replace-vehicle-quick-quote'
              : '/policies/replace-vehicle';
            this.store.dispatch(
              fromRouterActions.Go({
                path: [pathName, this.currentVehicle.id]
              })
            );
          } else {
            if (!hasNonComprehensiveNonLiabilityCoverage && !!this.currentVehicle) {
              this.store.dispatch(
                fromRouterActions.Go({
                  path: ['/policies/risk-selection', 'grace-period']
                })
              );
            } else {
              this.store
                .select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck)
                .pipe(
                  withLatestFrom(
                    this.hasMultipleAdvanceAutoPolicies$,
                    this.isPartner$,
                    this.hasClassicPolicies$,
                    this.hasAdvancePolicies$,
                    this.hasOnlyPolicyComprehensiveCoverage$,
                    this.hasAnyPolicyLiabilityCoverage$
                  ),
                  take(1)
                )
                .subscribe(
                  ([
                    isEligibleForAddVehicleFlow,
                    hasMultipleAdvanceAutoPolicies,
                    isPartner,
                    hasClassicPolicy,
                    advancePolicy,
                    hasOnlyPolicyComprehensiveCoverage,
                    hasAnyPolicyLiabilityCoverage
                  ]) => {
                    if (hasMultipleAdvanceAutoPolicies && !isPartner) {
                      this.store.dispatch(
                        new replaceVehicleActions.SetVehicleRiskSelectionContext(null)
                      );
                      this.store.dispatch(
                        fromRouterActions.Go({
                          path: ['/policies/risk-selection', 'multiple-policies']
                        })
                      );
                    } else {
                      const addVehicleSkipHardstop = this.featureFlagService.isEnabled(
                        'add_vehicle_skip_hardstop'
                      );
                      const isShowhardstopRiskSelectionPage =
                        this.featureFlagService.isEnabled('add_vehicle') &&
                        addVehicleSkipHardstop &&
                        advancePolicy &&
                        !isPartner &&
                        hasOnlyPolicyComprehensiveCoverage;

                      const classicOnlyAutoPolicy =
                        this.featureFlagService.isEnabled('add_vehicle') &&
                        hasClassicPolicy &&
                        !advancePolicy &&
                        !isPartner &&
                        addVehicleSkipHardstop;

                      let path: string[];
                      if (
                        this.featureFlagService.isEnabled('add_vehicle') &&
                        isEligibleForAddVehicleFlow &&
                        !isShowhardstopRiskSelectionPage
                      ) {
                        path = ['/policies/add-vehicle'];
                      } else if (classicOnlyAutoPolicy || isShowhardstopRiskSelectionPage) {
                        path = ['/policies/add-vehicle', 'year-vin'];
                      } else if (hasAnyPolicyLiabilityCoverage) {
                        path = ['/policies/add-vehicle', 'expectation'];
                      } else {
                        path = ['/policies/risk-selection', 'grace-period'];
                      }
                      this.store.dispatch(
                        fromRouterActions.Go({
                          path
                        })
                      );
                    }
                  }
                );
            }
          }
        }
      );
  }

  cancel() {
    // Route to history -1 ??
    this.store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
  }

  back() {
    this.store.dispatch(fromRouterActions.Back());
  }

  changeRisk(riskId) {
    this.riskSelectionForm.markAsDirty();
    this.currentVehicle = this.vehicles.find(risk => risk.id === riskId);
  }

  toggleGracePeriod() {
    this.displayPolicyGracePeriod = true;
  }

  onVisibleChange(e: boolean) {
    this.displayPolicyGracePeriod = e;
  }
}
